"use client";

import { useCallback, useMemo } from "react";

import type { PodcastEpisode, PodcastSeason } from "@/lib/podcast";

import { usePlayerActions, usePlayerState } from "./podcast-player";

const ACCENT = "#7074b7";

export const BRAND_COLOR = "#3d1d8f";
export const SEASON_COLORS = [
  "#1f4d8f", // S1 — deep ocean blue
  "#2e6240", // S2 — forest green
  "#7a4321", // S3 — warm rust
  "#6e2351", // S4 — berry / plum
  "#1d4769", // S5 — teal navy
];

export function seasonHex(seasonNumber: number | null | undefined): string {
  if (seasonNumber == null) return SEASON_COLORS[0];
  return SEASON_COLORS[(seasonNumber - 1) % SEASON_COLORS.length];
}

const POSITIONS: Array<{
  tx: number;
  ty: number;
  rot: number;
  scale: number;
}> = [
  { tx: 0, ty: 0, rot: 0, scale: 1 }, // 0 — front
  { tx: 5, ty: -4, rot: 5, scale: 0.94 }, // 1 — top-right peek
  { tx: -6, ty: 5, rot: -6, scale: 0.89 }, // 2 — bottom-left peek
  { tx: 8, ty: -7, rot: 8, scale: 0.84 }, // 3 — top-right deeper
  { tx: -9, ty: 8, rot: -9, scale: 0.8 }, // 4 — bottom-left deeper
];

// Cards beyond the explicit positions collapse onto the deepest one.
function positionFor(rank: number) {
  return POSITIONS[Math.min(rank, POSITIONS.length - 1)];
}

const CARD_TRANSITION =
  "transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]";

type StackCard =
  | { type: "brand" }
  | { type: "season"; season: PodcastSeason };

export function PodcastCover({
  title = "Fertility Talks",
  eyebrow = "Thérapie Fertility · Podcast",
  seasons,
  latestEpisode,
  className,
}: {
  title?: string;
  eyebrow?: string;
  seasons: PodcastSeason[];
  latestEpisode?: PodcastEpisode | null;
  className?: string;
}) {
  const player = usePlayerState();
  const actions = usePlayerActions();

  const brandTitle = title.replace(/\s*podcast\s*$/i, "").trim() || title;

  const onBrandClick = useCallback(() => {
    if (latestEpisode) actions.toggle(latestEpisode, latestEpisode.season);
  }, [actions, latestEpisode]);

  const playSeason = useCallback(
    (season: PodcastSeason) => {
      // If something from this season is already loaded, toggle it so
      // clicking the active card pauses without restarting.
      if (player.episode && player.seasonNumber === season.number) {
        actions.toggle(player.episode, season.number);
        return;
      }
      const first = season.episodes[0];
      if (first) actions.toggle(first, season.number);
    },
    [actions, player.episode, player.seasonNumber],
  );

  const cards = useMemo<StackCard[]>(
    () => [
      { type: "brand" },
      ...seasons.map((season) => ({ type: "season" as const, season })),
    ],
    [seasons],
  );

  const activeIndex = useMemo(() => {
    if (player.episode && player.seasonNumber != null) {
      const idx = seasons.findIndex((s) => s.number === player.seasonNumber);
      // +1 to skip the brand card at index 0.
      if (idx >= 0) return idx + 1;
    }
    return 0;
  }, [seasons, player.episode, player.seasonNumber]);

  return (
    <div className={`@container relative h-full w-full ${className ?? ""}`}>
      {cards.map((card, i) => {
        const rank = (i - activeIndex + cards.length) % cards.length;
        const isActive = rank === 0;
        const pos = positionFor(rank);

        if (card.type === "brand") {
          const brandLabel = latestEpisode
            ? player.episode?.id === latestEpisode.id && player.playing
              ? `Pause: ${latestEpisode.title}`
              : `Play latest episode: ${latestEpisode.title}`
            : "Fertility Talks";
          return (
            <SeasonCardShell
              key="brand"
              position={pos}
              zIndex={cards.length - rank}
              color={BRAND_COLOR}
              onClick={latestEpisode ? onBrandClick : undefined}
              ariaLabel={brandLabel}
              isActive={isActive}
            >
              <BrandFront title={brandTitle} eyebrow={eyebrow} isActive={isActive} />
            </SeasonCardShell>
          );
        }

        const season = card.season;
        const isCurrentEpisode =
          isActive &&
          player.episode != null &&
          player.seasonNumber === season.number;
        const seasonLabel = isCurrentEpisode
          ? player.playing
            ? `Pause: ${player.episode?.title ?? ""}`
            : `Play: ${player.episode?.title ?? ""}`
          : `Play Season ${season.number}`;

        return (
          <SeasonCardShell
            key={`s${season.number}`}
            position={pos}
            zIndex={cards.length - rank}
            color={seasonHex(season.number)}
            onClick={() => playSeason(season)}
            ariaLabel={seasonLabel}
            isActive={isActive}
          >
            {isCurrentEpisode ? (
              <NowPlayingFront
                episodeTitle={player.episode?.title ?? ""}
                episodeNumber={player.episode?.number ?? null}
                seasonNumber={season.number}
                isPlaying={player.playing}
              />
            ) : (
              <SeasonFront
                season={season}
                title={brandTitle}
                eyebrow={eyebrow}
                isActive={isActive}
              />
            )}
          </SeasonCardShell>
        );
      })}
    </div>
  );
}

function SeasonCardShell({
  children,
  position,
  zIndex,
  color,
  onClick,
  ariaLabel,
  isActive,
}: {
  children: React.ReactNode;
  position: { tx: number; ty: number; rot: number; scale: number };
  zIndex: number;
  color: string;
  onClick?: () => void;
  ariaLabel?: string;
  isActive: boolean;
}) {
  return (
    <div
      className={`absolute inset-0 overflow-hidden rounded-[28px] shadow-[0_20px_44px_-14px_rgba(20,10,60,0.5)] ${CARD_TRANSITION}`}
      style={{
        zIndex,
        transformOrigin: "center",
        transform: `translate(${position.tx}%, ${position.ty}%) rotate(${position.rot}deg) scale(${position.scale})`,
        background: `linear-gradient(135deg, ${lighten(color, 0.2)} 0%, ${color} 55%, ${darken(color, 0.4)} 100%)`,
      }}
      inert={!isActive}
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 22% 18%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 55%), radial-gradient(circle at 80% 95%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 50%)",
        }}
      />
      {children}
      {onClick && (
        <button
          type="button"
          onClick={onClick}
          aria-label={ariaLabel}
          tabIndex={-1}
          className="absolute inset-0 z-10 cursor-pointer rounded-[28px] transition-colors hover:bg-white/5"
        />
      )}
    </div>
  );
}

function BrandFront({
  title,
  eyebrow,
  isActive,
}: {
  title: string;
  eyebrow: string;
  isActive: boolean;
}) {
  return (
    <>
      <DecorRings cy={290} />
      {isActive && <DecorWaveform cy={410} />}
      <CardHeader
        eyebrow={eyebrow}
        title={splitTitleLines(title)}
        eyebrowColor="text-purple-01"
      />
      {isActive && <MicSpotlight />}
    </>
  );
}

function SeasonFront({
  season,
  title,
  eyebrow,
  isActive,
}: {
  season: PodcastSeason;
  title: string;
  eyebrow: string;
  isActive: boolean;
}) {
  return (
    <>
      <DecorRings cy={290} />
      {isActive && <DecorWaveform cy={410} />}
      <CardHeader
        eyebrow={`${eyebrow} · S${season.number}`}
        title={[`Season ${season.number}`, `${season.episodes.length} episodes`]}
        eyebrowColor="text-white/70"
      />
      {isActive && <MicSpotlight />}
      {!isActive && (
        <div className="absolute right-[clamp(1rem,6%,2.25rem)] bottom-[clamp(1rem,6%,2.25rem)] text-white">
          <span className="block text-right text-[clamp(2.5rem,12cqi,5rem)] font-extrabold leading-none opacity-80">
            S{season.number}
          </span>
        </div>
      )}
      <BrandWordmark title={title} />
    </>
  );
}

function NowPlayingFront({
  episodeTitle,
  episodeNumber,
  seasonNumber,
  isPlaying,
}: {
  episodeTitle: string;
  episodeNumber: number | null;
  seasonNumber: number;
  isPlaying: boolean;
}) {
  const animState = isPlaying ? "running" : "paused";
  return (
    <>
      <DecorRings cy={290} animState={animState} />
      <DecorWaveform cy={410} animState={animState} />

      <div className="absolute inset-x-[clamp(1rem,6%,2.25rem)] top-[clamp(1.25rem,6%,2.25rem)] text-white">
        <span className="flex items-center gap-2 text-caption font-semibold tracking-[0.18em] text-white uppercase @[24rem]:text-[11px] @[32rem]:text-[12px]">
          <span className="relative flex size-2 shrink-0">
            {isPlaying && (
              <span className="absolute inset-0 animate-ping rounded-full bg-white opacity-80" />
            )}
            <span className="relative size-2 rounded-full bg-white" />
          </span>
          Now playing · S{seasonNumber} · E{episodeNumber ?? "?"}
        </span>
        <h2 className="mt-3 line-clamp-4 text-h6 leading-[1.15] font-bold @[24rem]:text-h5 @[32rem]:text-[1.875rem] @[42rem]:text-[2.25rem]">
          {episodeTitle}
        </h2>
      </div>

      <MicSpotlight animState={animState} />

      <style>{ANIMATION_KEYFRAMES}</style>
    </>
  );
}

function CardHeader({
  eyebrow,
  title,
  eyebrowColor,
}: {
  eyebrow: string;
  title: string[];
  eyebrowColor: string;
}) {
  return (
    <div className="absolute inset-x-[clamp(1rem,6%,2.25rem)] top-[clamp(1.25rem,6%,2.25rem)] text-white">
      <span
        className={`${eyebrowColor} text-caption font-semibold tracking-[0.18em] uppercase @[24rem]:text-[11px] @[32rem]:text-[12px]`}
      >
        {eyebrow}
      </span>
      <h2 className="mt-3 text-h5 leading-none font-bold @[24rem]:text-h4 @[32rem]:text-h3 @[42rem]:text-h2">
        {title.map((line, i) => (
          <span key={i} className="block">
            {line}
          </span>
        ))}
      </h2>
    </div>
  );
}

function BrandWordmark({ title }: { title: string }) {
  return (
    <div className="text-purple-01/80 absolute bottom-[clamp(1rem,5%,1.75rem)] left-[clamp(1rem,5%,1.75rem)] text-caption font-semibold tracking-[0.16em] uppercase @[24rem]:text-[11px]">
      {title}
    </div>
  );
}

function DecorRings({
  cy,
  animState,
}: {
  cy: number;
  animState?: "running" | "paused";
}) {
  return (
    <svg
      viewBox="0 0 480 480"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="absolute inset-0 size-full"
      preserveAspectRatio="xMidYMid meet"
    >
      <g fill="none" strokeWidth="1.3" stroke="rgba(255,255,255,0.18)">
        {[80, 125, 170, 215, 260].map((r, i) => (
          <circle
            key={r}
            cx="240"
            cy={cy}
            r={r}
            style={
              animState
                ? {
                    animation: `pod-ring-pulse 2.4s ease-in-out infinite ${i * 0.18}s`,
                    animationPlayState: animState,
                  }
                : undefined
            }
          />
        ))}
      </g>
    </svg>
  );
}

function DecorWaveform({
  cy,
  animState,
}: {
  cy: number;
  animState?: "running" | "paused";
}) {
  return (
    <svg
      viewBox="0 0 480 480"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="absolute inset-0 size-full"
      preserveAspectRatio="xMidYMid meet"
    >
      <g fill="rgba(255,255,255,0.78)">
        {WAVEFORM_BARS.map((h, i) => {
          const x = 50 + i * 17.5;
          return (
            <rect
              key={i}
              x={x - 1.5}
              y={cy - h / 2}
              width={3}
              height={h}
              rx={1.5}
              style={
                animState
                  ? {
                      transformBox: "fill-box",
                      transformOrigin: "center",
                      animation: `pod-wave-bounce 1.4s ease-in-out infinite ${(i * 0.07).toFixed(2)}s`,
                      animationPlayState: animState,
                    }
                  : undefined
              }
            />
          );
        })}
      </g>
    </svg>
  );
}

function MicSpotlight({ animState }: { animState?: "running" | "paused" }) {
  return (
    <div
      className="absolute left-1/2 grid -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white shadow-[0_18px_40px_rgba(0,0,0,0.25)]"
      style={{
        top: "60%",
        width: "42%",
        height: "42%",
        animation: animState ? "pod-mic-glow 2.4s ease-in-out infinite" : undefined,
        animationPlayState: animState,
      }}
    >
      <FertilityTalksMark className="block h-[58%] w-auto" />
    </div>
  );
}

const ANIMATION_KEYFRAMES = `
  @keyframes pod-ring-pulse {
    0%, 100% { stroke-opacity: 0.18; transform: scale(1); transform-origin: 240px 290px; }
    50%      { stroke-opacity: 0.45; transform: scale(1.04); transform-origin: 240px 290px; }
  }
  @keyframes pod-wave-bounce {
    0%, 100% { transform: scaleY(0.85); }
    50%      { transform: scaleY(1.6); }
  }
  @keyframes pod-mic-glow {
    0%, 100% {
      box-shadow: 0 18px 40px rgba(0,0,0,0.25),
                  0 0 0 0 rgba(255,255,255,0);
    }
    50% {
      box-shadow: 0 18px 40px rgba(0,0,0,0.25),
                  0 0 36px 8px rgba(255,255,255,0.35);
    }
  }
`;

function splitTitleLines(title: string): string[] {
  const trimmed = title.trim();
  if (!trimmed.includes(" ")) return [trimmed];
  const mid = Math.floor(trimmed.length / 2);
  let breakAt = trimmed.indexOf(" ", mid);
  if (breakAt === -1) breakAt = trimmed.lastIndexOf(" ");
  if (breakAt === -1) return [trimmed];
  return [trimmed.slice(0, breakAt).trim(), trimmed.slice(breakAt).trim()];
}

const WAVEFORM_BARS = [
  18, 36, 14, 52, 28, 42, 68, 32, 18, 56, 38, 22, 60, 30, 46, 16, 44, 26, 50,
  20, 34, 14, 28,
];

// Keeps the gradient endpoints stylistically consistent across seasons.
function hexToRgb(hex: string): [number, number, number] {
  const cleaned = hex.replace("#", "");
  const n = parseInt(cleaned, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function rgbToHex(r: number, g: number, b: number): string {
  const to = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

function lighten(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
}

function darken(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

function FertilityTalksMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 291.58 460.79"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={className}
    >
      <g fill="#1a1330">
        <path d="M50.95,165.81c0-23.49-.29-46.99,.07-70.47,.55-36.04,16.33-64.04,47.34-82.14,59.34-34.63,134.02,2.35,142.74,70.24,.55,4.28,.78,8.63,.79,12.95,.06,46.82,.1,93.63,.05,140.45-.06,46.3-35,86.56-80.88,93.35-51.37,7.6-98.6-25.63-108.51-76.54-1.11-5.68-1.56-11.57-1.6-17.36-.18-23.49-.07-46.98-.07-70.47h.07Zm176.49-.47c.13-1.98,.32-3.44,.32-4.91,.01-21.48,.06-42.96-.05-64.44-.02-4.48-.35-9.01-1.1-13.42-6.8-40.07-40.29-68.22-80.95-68.2-43.4,.03-79.78,35.69-80.39,79.16-.32,22.48-.06,44.96-.04,67.44,0,1.42,.24,2.85,.38,4.37H227.44Z" />
        <path d="M265,183.65c-1.64-.98-3.03-1.61-4.17-2.54-2.84-2.29-3.95-5.31-3.13-8.9,.79-3.48,3.69-6.4,7.33-6.55,6.32-.27,12.67-.35,18.98,.02,3.55,.2,6.05,2.59,7.11,6.15,1.06,3.58,.31,7.11-2.74,8.91-3.43,2.02-3.58,4.65-3.57,7.92,.08,14.83,.19,29.67,0,44.5-.59,45.38-18.77,82.22-54.44,110.2-17.61,13.82-37.71,22.41-59.8,26.26-1.79,.31-3.57,.73-5.86,1.2v52.1c-3.87,1.36-29.24,1.67-36.35,.5v-52.32c-7.34-1.72-14.11-2.8-20.55-4.9-55.29-18.02-88.21-55.7-98.37-113-1.1-6.19-1.24-12.6-1.32-18.92-.18-15.16-.06-30.33-.06-45.5v-5.02c-1.35-.72-2.54-1.29-3.65-1.98-3.45-2.15-5-5.65-4.21-9.37,.73-3.42,3.55-6.49,7.23-6.68,6.48-.33,13-.36,19.47-.02,3.4,.18,5.74,2.69,6.76,5.93,1.09,3.46,.47,7.04-2.56,9.01-3.12,2.03-3.26,4.55-3.24,7.64,.07,15.5-.01,31,.05,46.5,.23,57.72,43.25,107.3,100.49,115.84,60.93,9.09,118.41-29.1,133.11-88.47,2.25-9.09,3.45-18.33,3.47-27.7,.04-15.33,.02-30.66,.02-46v-4.81h0Z" />
        <path d="M146.55,460.77h-38.48c-1.83,0-3.68,.02-5.49-.22-6.65-.88-11.42-6.44-11.37-13.11,.05-6.78,5.24-12.41,12.07-13.02,1.32-.12,2.66-.09,3.99-.09,26.15,0,52.3,0,78.45,.01,1.33,0,2.67-.04,3.99,.09,6.58,.65,11.63,6.09,11.84,12.72,.2,6.43-4.24,12.1-10.57,13.29-1.78,.33-3.64,.33-5.47,.33-12.99,.03-25.98,.01-38.98,.01h.02Z" />
        <path d="M147.05,95.82c15.98,0,31.95,0,47.93,.03,1.81,0,3.7,.09,5.41,.59,3.14,.91,4.81,3.61,4.65,6.86-.15,2.99-1.94,5.4-4.92,6.17-1.59,.41-3.29,.48-4.94,.48-32.62,.03-65.23,.03-97.85,.02-1.33,0-2.68,0-3.97-.27-3.46-.73-5.56-3.33-5.59-6.69-.03-3.44,1.9-6.04,5.41-6.79,1.76-.38,3.63-.37,5.45-.38,16.14-.03,32.28-.02,48.43,0h-.01Z" />
        <path d="M146.41,126.16h47.92c1.66,0,3.35-.06,4.98,.2,3.24,.51,5.43,2.92,5.73,6.01,.32,3.37-1.47,6.27-4.79,7.26-1.56,.46-3.27,.56-4.91,.56-32.61,.02-65.22,.02-97.83-.02-1.8,0-3.7-.14-5.38-.71-3.12-1.06-4.64-3.81-4.32-7.07,.29-2.97,2.21-5.29,5.23-5.91,1.61-.33,3.31-.31,4.96-.31,16.14-.02,32.28,0,48.42,0h0Z" />
        <path d="M146.78,66.52c10.32,0,20.64-.03,30.96,.01,5.84,.03,9.09,2.52,9.18,6.88,.09,4.52-3.27,7.09-9.41,7.09-20.64,0-41.29,0-61.93,0-1.16,0-2.34,.06-3.49-.09-3.59-.48-6.03-3.23-6.11-6.76-.08-3.53,2.23-6.36,5.85-6.99,1.3-.22,2.65-.15,3.98-.15,10.32-.01,20.64,0,30.96,0h0Z" />
        <path d="M146.53,37.22c5.66,0,11.32-.07,16.98,.02,4.89,.08,8.07,2.9,8.07,6.95s-3.15,6.99-8.07,7.03c-11.32,.09-22.64,.02-33.96,0-.83,0-1.68-.02-2.48-.19-3.59-.76-5.96-3.68-5.82-7.08,.15-3.49,2.58-6.25,6.3-6.59,3.3-.3,6.65-.14,9.98-.16,3-.02,5.99,0,8.99,0h0v.02Z" />
      </g>
      <path
        fill={ACCENT}
        fillRule="evenodd"
        d="M146.11,192.52c6.01,7.07,11.75,16.27,13.53,28.95,.83,6.46,.39,13.02-1.27,19.3-1.44,5.38-3.77,10.57-7.59,13.46-1.12,.84-2.98,1.68-4.99,1.81-2-.13-3.87-.97-4.99-1.81-3.82-2.88-6.15-8.08-7.59-13.46-1.67-6.27-2.1-12.84-1.27-19.3,1.81-12.9,7.72-22.2,13.85-29.33l.32,.38Zm45.21,14.76h-.28c-5.88,1.71-10.8,3.7-15.91,7.16-3.29,2.28-6.44,4.78-9.43,7.46-1.01,.91-2.73,1.99-2.95,2.77-.36,1.27,.06,3.25,0,4.83-.34,10.24-3.54,17.8-7.74,23.68-.25,.35-1.2,.88-.7,1.18,12.6-4.47,21.33-13.85,28.29-23.98,4.33-6.32,7.38-14.03,8.72-23.11Zm-91.06,0c1.34,9.07,4.38,16.79,8.72,23.11,6.96,10.14,15.69,19.51,28.29,23.98,.49-.28-.46-.83-.7-1.18-4.19-5.88-7.39-13.44-7.74-23.68-.06-1.58,.36-3.56,0-4.83-.22-.78-1.95-1.86-2.96-2.77-3-2.69-6.15-5.18-9.42-7.46-5.11-3.47-10.03-5.45-15.91-7.16h-.28Zm79.32,32.76c-5.67,6.25-11.9,11.95-19.54,15.94-1.15,.59-2.46,.94-3.24,1.75,15.43,1.08,30.88-1.72,45.04-8.19,3.5-1.58,6.81-3.6,9.85-5.99,.36-.28,1.34-.62,.84-.87-8.57-3.63-21.68-3.8-32.95-2.63Zm-67.59,0c-11.27-1.16-24.37-1.01-32.97,2.63-.49,.25,.49,.59,.84,.87,3.04,2.39,6.36,4.41,9.85,5.99,14.16,6.46,29.62,9.27,45.04,8.19-.78-.83-2.1-1.16-3.23-1.75-7.63-3.99-13.85-9.69-19.52-15.94Zm92.79,21.19c-3.12-1.85-7.18-4.48-10.84-5.27-.2-.04-.39,.1-.56,.14-4.41,1.41-8.94,2.44-13.5,3.07-4.43,.63-8.95,.69-13.53,1.04-.46,.03-1.5,.04-1.69,.43-.42,1.46,.69,2,1.41,2.63,.73,.63,1.68,1.37,2.39,2.04,6.29,.78,13.5,2.23,20.54,1.9,6.44-.27,12.8-1.54,18.86-3.8-.71-.98-1.96-1.54-3.05-2.18h-.04Zm-118-.01h-.01c-1.11,.66-2.34,1.23-3.05,2.2,6.06,2.25,12.42,3.54,18.86,3.8,7.04,.32,14.26-1.12,20.54-1.9,.71-.67,1.67-1.41,2.39-2.04,.73-.63,1.83-1.18,1.41-2.63-.18-.41-1.22-.41-1.69-.43-4.58-.35-9.1-.41-13.53-1.04-4.58-.63-9.09-1.65-13.5-3.07-.18-.06-.38-.18-.56-.14-3.68,.78-7.74,3.43-10.87,5.27Zm69.76,10.11c3.64,1.39,8.86,2.6,13.65,1.46-3.29-7.49-9.41-12.04-17.73-14.33,2.06,3.46,4.64,7.13,4.08,12.87Zm-21.51,0c-.56-5.74,2-9.4,4.09-12.86-8.32,2.27-14.44,6.83-17.73,14.33,4.79,1.12,10-.1,13.64-1.47Zm10.76,12.39s.03,.01,.04,.03c2.94-2.38,6.99-6.96,7.2-12.13h-.01c.18-5.35-2.14-11.32-5.77-12.59-.5-.18-.99-.27-1.46-.28-.46,.01-.95,.11-1.46,.28-3.63,1.27-5.95,7.24-5.77,12.58h-.01c.2,5.18,4.24,9.76,7.2,12.14,.01-.01,.03-.01,.04-.03Z"
      />
    </svg>
  );
}
